import SEOHelmet from '@/components/SEOHelmet';
import { FC } from 'react';
import styles from './users-management.module.scss';
import ChevronLeftIcon from '@/assets/icons/chevron-left.svg?react';
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { classnames } from '@/utils/functions';
import MsgParagraph from '@/components/MsgParagraph';
import { User } from '@/models/user';
import fetchUsers from '@/api/users/fetchUsers';

const UsersManagement = () => {
  const [query, setQuery] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sortDesc, setSortDesc] = useState<boolean>(true);
  const [sortColumn, setSortColumn] = useState<keyof User>('date');
  const columns: { label: string; value: keyof User }[] = [
    { label: 'Date', value: 'date' },
    { label: 'Email', value: 'email' },
    { label: 'Name', value: 'name' },
    { label: 'Organization', value: 'organization' },
    { label: 'Status', value: 'status' },
  ];
  const getAllUsers = async () => {
    try {
      setLoading(true);

      const fetchedUsers = await fetchUsers();

      setUsers(fetchedUsers);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onSearch: (
    e: FormEvent<HTMLFormElement> | ChangeEvent<HTMLInputElement>
  ) => void = (e) => {
    let search = '';
    if (e.type === 'submit') {
      e.preventDefault();
      const formData = new FormData(
        (e as FormEvent<HTMLFormElement>).currentTarget
      );
      search = formData.get('search') as string;
    } else {
      search = (e as ChangeEvent<HTMLInputElement>).target.value;
    }

    setQuery(search);
  };

  const onSortingChange = (column: keyof User) => {
    setSortColumn(column);
    if (sortColumn === column) {
      setSortDesc((current) => !current);
    } else {
      setSortDesc(true);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(query.toLowerCase()) ||
        user.name.toLowerCase().includes(query.toLowerCase()) ||
        user.organization.toLowerCase().includes(query.toLowerCase())
    );
  }, [users, query]);

  const sortedUsers = useMemo(() => {
    return filteredUsers.sort((a, b) => {
      let result = 0;

      if (a[sortColumn] < b[sortColumn]) {
        result = -1;
      } else if (a[sortColumn] > b[sortColumn]) {
        result = 1;
      } else {
        return b.date.getTime() - a.date.getTime();
      }

      return (sortColumn === 'date' ? !sortDesc : sortDesc) ? result : -result;
    });
  }, [filteredUsers, sortColumn, sortDesc]);

  useEffect(() => {
    getAllUsers();
  }, []);
  return (
    <div className={styles['users-management-wrapper']}>
      <SEOHelmet title="User Management" />
      <h1>Users Management</h1>

      {loading ? (
        <MsgParagraph>Loading...</MsgParagraph>
      ) : (
        <>
          <form onSubmit={onSearch}>
            <input
              type="search"
              id="search"
              name="search"
              placeholder="Type to filter by email, name, organization or use case keywords.."
              onChange={onSearch}
              required
            />
          </form>
          {query !== '' && status === 'success' && sortedUsers.length === 0 ? (
            <MsgParagraph>
              Search for<strong>"{query}"</strong> returned no results.
            </MsgParagraph>
          ) : (
            <>
              <p className={styles['counter']}>
                {query
                  ? `Filter matches ${filteredUsers.length} of ${users.length} Users`
                  : `${users.length} Total Users`}
              </p>
              <div className={styles['table-wrapper']}>
                <table>
                  <thead>
                    <tr>
                      {columns.map(({ label, value }) => (
                        <th key={value}>
                          <SortingButton
                            column={value}
                            label={label}
                            currentColumn={sortColumn}
                            desc={sortDesc}
                            currentDesc={sortDesc}
                            onClick={() => onSortingChange(value)}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(
                      ({ id, email, name, organization, date, status }) => (
                        <tr key={id} id={id}>
                          <td>{date.toLocaleString()}</td>
                          <td>
                            <p className={styles['email-cell']}>
                              {email.split('@').map((part, index) => {
                                return (
                                  <span key={index}>
                                    {index === 1 && '@'}
                                    {part}
                                  </span>
                                );
                              })}
                            </p>
                          </td>
                          <td>{name}</td>
                          <td>
                            <strong>{organization}</strong>
                          </td>
                          <td
                            className={classnames(
                              styles['status'],
                              styles[`status-${status.toLowerCase()}`]
                            )}
                          >
                            <strong>{status}</strong>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

interface SortingButtonProps {
  column: keyof User;
  label: string;
  currentColumn: keyof User;
  desc: boolean;
  currentDesc: boolean;
  onClick: () => void;
}
const SortingButton: FC<SortingButtonProps> = ({
  column,
  label,
  currentColumn,
  currentDesc,
  onClick,
}) => {
  return (
    <button
      className={classnames(styles['sorting-button'], {
        [styles['active-col']]: currentColumn === column,
        [styles['is-asc']]: !currentDesc,
      })}
      onClick={onClick}
    >
      {label}

      <ChevronLeftIcon className={styles['sorting-order-icon']} />
    </button>
  );
};

export default UsersManagement;
